import { ReactElement, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useNavigate } from 'react-router-dom'

import Dropdown from 'components/v2/Dropdown'
import List from 'components/v2/List'
import {
  ArrowRightIcon,
  DotsIcon,
  ForbiddenIcon,
  NoteIcon,
  PencilIcon,
  ResendEmailIcon,
  ShieldIcon,
  ShieldLessIcon,
  TaxIcon
} from 'components/Icons'
import { ApplyBookingInsuranceModal } from 'features/Bookings/components/ApplyBookingInsuranceModal'
import { CancelBookingInsuranceModal } from 'features/Bookings/components/CancelBookingInsuranceModal'
import { CancelTicketsModal } from 'features/Bookings/components/CancelTicketsModal'
import { ResendEmailsModal } from 'features/Bookings/components/ResendEmailsModal'
import { RegisterSupportInteractionModal } from 'features/Bookings/components/RegisterSupportInteractionModal'
import {
  canApplyInsurance as userCanApplyInsurance,
  canCancelInsurance as userCanCancelInsurance,
  canCancelTickets,
  canEditBooking,
  canMoveBookingTrip,
  canRegisterSupportInteraction,
  canSendEmailBooking,
  canRegisterBookingTaxInvoiced
} from 'features/Users/utils'
import { useLogin } from 'features/Login/hooks'
import IconButton from 'components/v2/IconButton'
import { IBookingModel } from 'features/Bookings/types'
import { RegisterBookingTaxInvoicedModal } from 'features/Bookings/components/RegisterBookingTaxInvoicedModal'
import { COUNTRY_CODES } from 'utils/countries'
import { EBookingActions } from 'features/Bookings/models'

export const BookingActions = ({ booking }: { booking: IBookingModel }): ReactElement => {
  const navigate = useNavigate()

  const [isOpenDropdown, setOpenDropdown] = useState(false)
  const [isOpenCancelTicketsModal, setOpenCancelTicketsModal] = useState(false)
  const [isOpenResendEmailsModal, setOpenResendEmailsModal] = useState(false)
  const [isOpenApplyInsuranceModal, setOpenApplyInsuranceModal] = useState(false)
  const [isOpenCancelInsuranceModal, setOpenCancelInsuranceModal] = useState(false)
  const [isOpenRegisterSupportInteractionModal, setOpenRegisterSupportInteractionModal] = useState(false)
  const [isOpenRegisterBookingTaxInvoicedModal, setOpenRegisterBookingTaxInvoicedModal] = useState(false)

  const {
    accessToken: { policies }
  } = useLogin()

  const toggleDropdown = (): void => {
    setOpenDropdown(!isOpenDropdown)
  }

  const bookingActions = booking.getBookingsActions()
  const hasReservation = booking.hasReservation
  const canBeCancelled =
    canCancelTickets(policies) &&
    (hasReservation || booking.hasPaidStatus) &&
    bookingActions.includes(EBookingActions.Cancellation)
  const isUSBooking = booking.country.code === COUNTRY_CODES.US
  const canEdit =
    canEditBooking(policies) && hasReservation && !isUSBooking && bookingActions.includes(EBookingActions.EditBooking)
  const canMoveTrip =
    canMoveBookingTrip(policies) && hasReservation && bookingActions.includes(EBookingActions.MoveBooking)
  const insuranceCanBeApplied =
    userCanApplyInsurance(policies) &&
    booking.canApplyInsurance &&
    bookingActions.includes(EBookingActions.ApplyInsurance)
  const insuranceCanBeCancelled =
    userCanCancelInsurance(policies) &&
    booking.canCancelInsurance &&
    bookingActions.includes(EBookingActions.CancelInsurance)
  const canResendEmail =
    canSendEmailBooking(policies) && hasReservation && bookingActions.includes(EBookingActions.ResendEmails)
  const canAddNote =
    canRegisterSupportInteraction(policies) && hasReservation && bookingActions.includes(EBookingActions.Notes)
  const isRegisterBookingTaxInvoicedEnabled = canRegisterBookingTaxInvoiced(policies) && booking.isTaxInvoiceable

  const handleApplyInsurance = (): void => {
    if (insuranceCanBeApplied) {
      setOpenDropdown(false)
      setOpenApplyInsuranceModal(true)
    }
  }

  const handleCancelInsurance = (): void => {
    if (insuranceCanBeCancelled) {
      setOpenDropdown(false)
      setOpenCancelInsuranceModal(true)
    }
  }

  const handleRegisterBookingTaxInvoiced = (): void => {
    if (isRegisterBookingTaxInvoicedEnabled) {
      setOpenDropdown(false)
      setOpenRegisterBookingTaxInvoicedModal(true)
    }
  }

  return (
    <>
      <Dropdown.Wrapper noPadding noIndicator onClickAway={(): void => setOpenDropdown(false)}>
        <IconButton icon={<DotsIcon />} onClick={toggleDropdown} noBorder smallPadding />
        <Dropdown.Content open={isOpenDropdown} noIndicator noOverflow left>
          <List.Container>
            <List.Item
              action
              onClick={(): TSFixMe => {
                if (canBeCancelled) {
                  setOpenDropdown(false)
                  setOpenCancelTicketsModal(true)
                }
              }}
              disabled={!canBeCancelled}
            >
              <List.Icon>
                <ForbiddenIcon />
              </List.Icon>
              <FormattedMessage id="dictionary.cancellation" defaultMessage="Cancellation" />
            </List.Item>
            <List.Item
              action
              onClick={(): TSFixMe => {
                if (canResendEmail) {
                  setOpenDropdown(false)
                  setOpenResendEmailsModal(true)
                }
              }}
              disabled={!canResendEmail}
            >
              <List.Icon>
                <ResendEmailIcon />
              </List.Icon>
              <FormattedMessage id="dictionary.resendEmails" defaultMessage="Resend Emails" />
            </List.Item>
            <List.Item
              action
              onClick={(): void => {
                if (canEdit) {
                  navigate(`/bookings/${booking.id}`)
                }
              }}
              disabled={!canEdit}
            >
              <List.Icon>
                <PencilIcon />
              </List.Icon>
              <FormattedMessage id="bookings.list.actions.edit" defaultMessage="Edit booking" />
            </List.Item>
            <List.Item
              action
              onClick={(): TSFixMe => {
                if (canMoveTrip) {
                  navigate(`/bookings/${booking.id}/move`)
                }
              }}
              disabled={!canMoveTrip}
            >
              <List.Icon>
                <ArrowRightIcon />
              </List.Icon>
              <FormattedMessage id="bookings.list.actions.move" defaultMessage="Move booking" />
            </List.Item>
            <List.Item action onClick={handleApplyInsurance} disabled={!insuranceCanBeApplied}>
              <List.Icon>
                <ShieldIcon />
              </List.Icon>
              <FormattedMessage id="bookings.list.actions.applyInsurance" defaultMessage="Apply insurance" />
            </List.Item>
            <List.Item
              action
              onClick={handleRegisterBookingTaxInvoiced}
              disabled={!isRegisterBookingTaxInvoicedEnabled}
            >
              <List.Icon>
                <TaxIcon />
              </List.Icon>
              <FormattedMessage id="bookings.list.actions.invoiced" defaultMessage="Invoiced" />
            </List.Item>
            <List.Item action onClick={handleCancelInsurance} disabled={!insuranceCanBeCancelled}>
              <List.Icon>
                <ShieldLessIcon />
              </List.Icon>
              <FormattedMessage id="bookings.list.actions.cancelInsurance" defaultMessage="Cancel insurance" />
            </List.Item>
            <List.Item
              action
              onClick={(): void => {
                if (canAddNote) {
                  setOpenDropdown(false)
                  setOpenRegisterSupportInteractionModal(true)
                }
              }}
              disabled={!canAddNote}
            >
              <List.Icon>
                <NoteIcon />
              </List.Icon>
              <FormattedMessage id="bookings.list.actions.registerSupportInteraction" defaultMessage="Notes" />
            </List.Item>
          </List.Container>
        </Dropdown.Content>
      </Dropdown.Wrapper>
      {isOpenCancelTicketsModal && (
        <CancelTicketsModal
          booking={booking as IBookingModel}
          closeModal={(): void => setOpenCancelTicketsModal(false)}
        />
      )}
      {isOpenResendEmailsModal && (
        <ResendEmailsModal booking={booking} closeModal={(): void => setOpenResendEmailsModal(false)} />
      )}
      {isOpenApplyInsuranceModal && (
        <ApplyBookingInsuranceModal booking={booking} closeModal={(): void => setOpenApplyInsuranceModal(false)} />
      )}
      {isOpenRegisterSupportInteractionModal && (
        <RegisterSupportInteractionModal
          booking={booking}
          closeModal={(): void => setOpenRegisterSupportInteractionModal(false)}
        />
      )}
      {isOpenCancelInsuranceModal && (
        <CancelBookingInsuranceModal booking={booking} closeModal={(): void => setOpenCancelInsuranceModal(false)} />
      )}
      {isOpenRegisterBookingTaxInvoicedModal && (
        <RegisterBookingTaxInvoicedModal
          booking={booking}
          closeModal={(): void => setOpenRegisterBookingTaxInvoicedModal(false)}
        />
      )}
    </>
  )
}
